/*
 * Sign-in ("Split") styles for app.fluvius.co.
 *
 * These tokens are the marketing site's own palette and are deliberately kept
 * out of assets/jss/material-dashboard-react.js — that file is the Material
 * Dashboard palette shared with the authenticated app, a different design
 * language altogether.
 *
 * NOTE ON !important: assets/css/material-dashboard-react.css is imported
 * globally and carries `* { letter-spacing: normal !important }` plus an
 * `h1..h6 { font-family:'Montserrat'!important; font-weight:600!important }`.
 * JSS emits single-class selectors, which lose to !important no matter the
 * order, so every letter-spacing here — and the heading font — has to shout
 * back. Do not drop these; the typographic identity of the page depends on
 * them.
 */

const cream = '#FBFAF7';
const ink = '#232B27';
const teal = '#0E5B53';
const tealHover = '#148F82';
const panelDark = '#0B3A35';
const mint = '#7FD1C6';
const onDark = '#F3FAF8';
const rust = '#B4551F';
const line = 'rgba(14,74,68,0.35)';
const ring = '0 0 0 3px rgba(127,209,198,0.28)';
const ringBad = '0 0 0 3px rgba(180,85,31,0.2)';

const sans = "'Schibsted Grotesk', system-ui, sans-serif";
const mono = "'IBM Plex Mono', monospace";

const phone = '@media (max-width:1023px)';

const loginStyle = {
  shell: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    // JSS joins a plain array into one space-separated value, so the 100vh
    // fallback for browsers without dvh has to go through `fallbacks`.
    fallbacks: [{ minHeight: '100vh' }],
    minHeight: '100dvh',
    boxSizing: 'border-box',
    background: cream,
    fontFamily: sans,
    fontWeight: 400,
    lineHeight: 'normal',
    color: ink,
    '& *': {
      boxSizing: 'border-box',
    },
    [phone]: {
      display: 'block',
      minHeight: 0,
    },
  },

  // ── left: the dark panel ────────────────────────────────────────────────
  panel: {
    position: 'relative',
    background: panelDark,
    padding: '52px 56px',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    overflow: 'hidden',
    minHeight: 640,
    [phone]: {
      display: 'none',
    },
  },

  /*
   * Decorative preview of the hub behind the copy. Anchored to the panel's own
   * top and bottom rather than to fixed offsets, so it keeps sitting in the two
   * free bands — above the H1 and below the list — at any viewport height.
   */
  preview: {
    position: 'absolute',
    top: 0,
    right: 0,
    bottom: 0,
    left: 0,
    opacity: 0.34,
    pointerEvents: 'none',
  },
  pvCard: {
    position: 'absolute',
    left: 40,
    right: 40,
    background: 'rgba(243,250,248,0.07)',
    border: '1px solid rgba(127,209,198,0.22)',
  },
  pvTop: {
    top: 96,
    padding: '16px 20px',
    display: 'flex',
    alignItems: 'center',
    gap: 26,
  },
  pvBot: {
    bottom: 93,
    padding: '18px 20px',
    display: 'flex',
    alignItems: 'flex-end',
    gap: 26,
  },
  pvK: {
    fontFamily: mono,
    fontSize: 11,
    letterSpacing: '0.12em !important',
    textTransform: 'uppercase',
    color: mint,
  },
  pvRule: {
    width: 1,
    alignSelf: 'stretch',
    background: 'rgba(127,209,198,0.22)',
  },
  pvAv: {
    width: 30,
    height: 30,
    borderRadius: 999,
    background: 'rgba(127,209,198,0.3)',
    flex: '0 0 auto',
  },
  pvSum: {
    fontSize: 40,
    fontWeight: 500,
    letterSpacing: '-0.02em !important',
    color: 'rgba(243,250,248,0.95)',
    marginTop: 8,
  },
  pvChart: {
    flex: 1,
    display: 'flex',
    alignItems: 'flex-end',
    gap: 7,
    height: 78,
  },
  pvBar: {
    flex: 1,
    background: 'rgba(127,209,198,0.45)',
  },
  pvBarNow: {
    flex: 1,
    background: mint,
  },
  pvBarFc: {
    flex: 1,
    background: 'rgba(127,209,198,0.12)',
    border: '1px dashed rgba(127,209,198,0.7)',
  },
  pvGrow: {
    flex: 1,
  },
  pvFixed: {
    flex: '0 0 auto',
  },
  pvRow: {
    display: 'flex',
    gap: 22,
    marginTop: 11,
  },
  pvItem: {
    fontSize: 13.5,
    color: 'rgba(243,250,248,0.85)',
  },
  pvPaid: {
    color: mint,
  },
  pvDue: {
    color: 'rgba(243,250,248,0.6)',
  },
  pvTeam: {
    display: 'flex',
    alignItems: 'center',
    gap: 11,
  },
  pvTeamName: {
    fontSize: 13.5,
    color: 'rgba(243,250,248,0.88)',
  },
  pvTeamNote: {
    fontSize: 12,
    color: 'rgba(243,250,248,0.55)',
  },
  pvHead: {
    display: 'flex',
    alignItems: 'baseline',
    gap: 12,
  },
  pvPlan: {
    fontFamily: mono,
    fontSize: 11,
    letterSpacing: '0.1em !important',
    color: 'rgba(243,250,248,0.6)',
  },

  /*
   * Dense where the H1 and the list sit, lighter at the foot so the spend
   * figure and a column or two stay readable as texture.
   */
  scrim: {
    position: 'absolute',
    top: 0,
    right: 0,
    bottom: 0,
    left: 0,
    pointerEvents: 'none',
    background: 'linear-gradient(180deg,rgba(11,58,53,0.72) 0%,rgba(11,58,53,0.9) 30%,rgba(11,58,53,0.88) 62%,rgba(11,58,53,0.5) 100%)',
  },

  /*
   * .preview and .scrim are absolutely positioned and so out of flow; the
   * brand, pitch and copyright each take position:relative to paint above the
   * scrim by DOM order. There is deliberately no z-index anywhere on this page.
   */
  brand: {
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    gap: 11,
    alignSelf: 'flex-start',
    textDecoration: 'none',
    '& img': {
      display: 'block',
      width: 'auto',
    },
  },
  mark: {
    height: 24,
    [phone]: {
      height: 22,
    },
  },
  word: {
    height: 17,
    [phone]: {
      height: 15,
    },
  },
  tag: {
    fontFamily: mono,
    fontSize: 11,
    letterSpacing: '0.14em !important',
    textTransform: 'uppercase',
    color: mint,
    borderLeft: '1px solid rgba(127,209,198,0.4)',
    paddingLeft: 11,
    marginLeft: 3,
    [phone]: {
      fontSize: 10.5,
      paddingLeft: 10,
    },
  },

  pitch: {
    position: 'relative',
  },
  pitchTitle: {
    fontFamily: `${sans} !important`,
    fontSize: 46,
    lineHeight: 1.05,
    fontWeight: '500 !important',
    letterSpacing: '-0.03em !important',
    margin: 0,
    color: onDark,
    maxWidth: 440,
    textWrap: 'balance',
  },
  dot: {
    color: mint,
  },
  inside: {
    display: 'flex',
    flexDirection: 'column',
    marginTop: 34,
    maxWidth: 450,
  },
  insideRow: {
    display: 'grid',
    gridTemplateColumns: '26px 1fr',
    gap: 14,
    padding: '15px 0',
    borderTop: '1px solid rgba(127,209,198,0.25)',
  },
  insideN: {
    fontFamily: mono,
    fontSize: 11.5,
    color: mint,
    paddingTop: 3,
  },
  insideT: {
    fontSize: 16.5,
    fontWeight: 600,
    color: onDark,
  },
  insideD: {
    fontSize: 14.5,
    lineHeight: 1.5,
    color: 'rgba(243,250,248,0.66)',
    marginTop: 4,
  },

  copyright: {
    position: 'relative',
    fontFamily: mono,
    fontSize: 11.5,
    letterSpacing: '0.1em !important',
    color: 'rgba(243,250,248,0.55)',
    alignSelf: 'flex-start',
    textDecoration: 'none',
    '&:hover': {
      color: mint,
    },
  },

  // ── phone header, standing in for the panel ─────────────────────────────
  mhead: {
    display: 'none',
    [phone]: {
      display: 'block',
      background: panelDark,
      padding: 'calc(env(safe-area-inset-top) + 30px) 22px 30px',
    },
  },
  mheadTitle: {
    fontFamily: `${sans} !important`,
    fontSize: 30,
    lineHeight: 1.1,
    fontWeight: '500 !important',
    letterSpacing: '-0.028em !important',
    color: onDark,
    margin: '26px 0 0',
  },

  // ── right: the form ─────────────────────────────────────────────────────
  side: {
    background: cream,
    padding: '52px 72px',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    [phone]: {
      padding: '30px 22px calc(env(safe-area-inset-bottom) + 44px)',
      display: 'block',
    },
  },
  form: {
    maxWidth: 400,
    width: '100%',
    [phone]: {
      maxWidth: 'none',
    },
  },
  kicker: {
    fontFamily: mono,
    fontSize: 11.5,
    letterSpacing: '0.14em !important',
    textTransform: 'uppercase',
    color: teal,
    [phone]: {
      display: 'none',
    },
  },
  formTitle: {
    fontFamily: `${sans} !important`,
    fontSize: 32,
    lineHeight: 1.1,
    fontWeight: '500 !important',
    letterSpacing: '-0.025em !important',
    margin: '12px 0 0',
    color: ink,
    [phone]: {
      display: 'none',
    },
  },
  lede: {
    fontSize: 15.5,
    lineHeight: 1.55,
    color: 'rgba(35,43,39,0.62)',
    margin: '10px 0 0',
    [phone]: {
      display: 'none',
    },
  },

  field: {
    marginTop: 20,
  },
  /*
   * The reference build wrote this as `.field:first-of-type`, which never
   * matches — the form's first div child is the kicker, so :first-of-type
   * lands there instead. This is the rule it meant to write, keyed explicitly.
   */
  fieldFirst: {
    marginTop: 32,
    [phone]: {
      marginTop: 0,
    },
  },
  lab: {
    display: 'block',
    fontFamily: mono,
    fontSize: 11,
    fontWeight: 400,
    lineHeight: 'normal',
    letterSpacing: '0.12em !important',
    textTransform: 'uppercase',
    color: 'rgba(35,43,39,0.6)',
    marginBottom: 8,
  },
  box: {
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    background: '#fff',
    border: `1px solid ${line}`,
    height: 54,
    transition: 'border-color .14s ease, box-shadow .14s ease',
    '&:focus-within': {
      borderColor: teal,
      boxShadow: ring,
    },
    [phone]: {
      height: 56,
    },
  },
  // error — the same rust the site uses for overdue and failure states
  boxBad: {
    borderColor: rust,
    '&:focus-within': {
      borderColor: rust,
      boxShadow: ringBad,
    },
  },
  input: {
    flex: 1,
    minWidth: 0,
    height: '100%',
    border: 'none',
    outline: 'none',
    background: 'transparent',
    padding: '0 16px',
    fontFamily: 'inherit',
    fontSize: 16,
    color: ink,
    '&::placeholder': {
      color: 'rgba(35,43,39,0.38)',
    },
    [phone]: {
      fontSize: 16.5,
    },
  },
  peek: {
    border: 'none',
    background: 'transparent',
    padding: '0 16px',
    height: '100%',
    cursor: 'pointer',
    fontFamily: mono,
    fontSize: 11,
    letterSpacing: '0.1em !important',
    textTransform: 'uppercase',
    color: teal,
    '&:hover': {
      color: tealHover,
    },
  },
  err: {
    fontSize: 13.5,
    lineHeight: 1.5,
    color: rust,
    marginTop: 7,
    display: 'none',
  },
  errOn: {
    display: 'block',
  },

  go: {
    width: '100%',
    height: 56,
    marginTop: 26,
    border: 'none',
    background: teal,
    color: cream,
    fontFamily: sans,
    fontSize: 16.5,
    fontWeight: 600,
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 10,
    transition: 'background .14s ease',
    '&:hover': {
      background: tealHover,
    },
    /*
     * The global stylesheet kills the native focus ring with
     * `button:focus { outline: 0 !important }`, so the button paints the
     * design's own mint ring instead of being left with no focus state.
     */
    '&:focus-visible': {
      boxShadow: ring,
    },
    '&:disabled': {
      opacity: 0.7,
      cursor: 'default',
      background: teal,
    },
    [phone]: {
      height: 58,
      fontSize: 17,
      marginTop: 24,
    },
  },

  foot: {
    marginTop: 32,
    paddingTop: 20,
    borderTop: '1px solid rgba(14,74,68,0.18)',
    fontSize: 14,
    lineHeight: 1.6,
    color: 'rgba(35,43,39,0.6)',
    [phone]: {
      marginTop: 28,
      fontSize: 14.5,
    },
  },
  // Phone-only: the copyright line the dark panel carries on desktop.
  mfoot: {
    display: 'none',
    [phone]: {
      display: 'block',
      marginTop: 26,
      fontFamily: mono,
      fontSize: 11,
      letterSpacing: '0.1em !important',
      color: 'rgba(35,43,39,0.45)',
    },
  },

  '@media (prefers-reduced-motion:reduce)': {
    box: {
      transition: 'none !important',
    },
    go: {
      transition: 'none !important',
    },
  },
};

export default loginStyle;
